/**
 * AccountSettings — Profile, password, preferences, sign out.
 *
 * Route: /#/settings/account
 *
 * Sections:
 *   - Profile: edit display name; email/role/tenant read-only
 *   - Change password: current + new + confirm via Supabase auth.updateUser
 *   - Notification preferences: localStorage-backed toggles
 *   - Display preferences: default landing dashboard + table density
 *   - Sign out: large clear button
 *
 * Prefs live in localStorage today (key prefix `aip:pref:`). Promote to a
 * user_preferences table when we need cross-device sync.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import { tokens } from "@/components/operator/tokens";
import { getSession, supabase, logout, loadNotificationPrefs, saveNotificationPrefs, type NotificationPrefs } from "@/lib/session";

type Density = "comfortable" | "compact";
type LandingRole = "auto" | "manager" | "ae" | "bdr" | "executive" | "marketing" | "se";

const PREF_KEYS = {
  density: "aip:pref:density",
  landing: "aip:pref:landing",
} as const;

function readBoolPref(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    return v === "1" || v === "true";
  } catch { return fallback; }
}
function writeBoolPref(key: string, val: boolean) {
  try { localStorage.setItem(key, val ? "1" : "0"); } catch { /* ignore */ }
}
function readStringPref<T extends string>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return (v as T) || fallback;
  } catch { return fallback; }
}
function writeStringPref(key: string, val: string) {
  try { localStorage.setItem(key, val); } catch { /* ignore */ }
}

export default function AccountSettings() {
  const session = getSession();
  const [, setLocation] = useLocation();

  // -------- Profile --------
  const [fullName, setFullName] = useState<string>(session?.name || "");
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  async function saveProfile() {
    if (!session) return;
    if (!fullName.trim()) {
      setProfileMsg({ kind: "err", text: "Display name cannot be empty." });
      return;
    }
    setProfileBusy(true);
    setProfileMsg(null);
    try {
      const sb = supabase();
      const { error } = await sb.from("users").update({ full_name: fullName.trim() }).eq("id", session.id);
      if (error) throw new Error(error.message);
      setProfileMsg({ kind: "ok", text: "Display name saved. Refresh to see it everywhere." });
    } catch (err: any) {
      setProfileMsg({ kind: "err", text: err?.message || "Could not save name." });
    } finally {
      setProfileBusy(false);
    }
  }

  // -------- Password --------
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  async function changePassword() {
    setPwMsg(null);
    if (!session) return;
    if (newPw.length < 8) { setPwMsg({ kind: "err", text: "New password must be at least 8 characters." }); return; }
    if (newPw !== confirmPw) { setPwMsg({ kind: "err", text: "New passwords do not match." }); return; }
    if (!currentPw) { setPwMsg({ kind: "err", text: "Enter your current password." }); return; }
    setPwBusy(true);
    try {
      const sb = supabase();
      // Re-authenticate first so the change is intentional.
      const { error: reauthErr } = await sb.auth.signInWithPassword({
        email: session.email,
        password: currentPw,
      });
      if (reauthErr) throw new Error("Current password is incorrect.");
      const { error } = await sb.auth.updateUser({ password: newPw });
      if (error) throw new Error(error.message);
      setPwMsg({ kind: "ok", text: "Password updated." });
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err: any) {
      setPwMsg({ kind: "err", text: err?.message || "Could not change password." });
    } finally {
      setPwBusy(false);
    }
  }

  // -------- Notification prefs (server-backed, user_notification_prefs table) --------
  const [prefs, setPrefs] = useState<NotificationPrefs>({
    daily_digest: true, escalations: true, weekly_rollup: true, support_alerts: true,
  });
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [prefMsg, setPrefMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!session) { setPrefsLoaded(true); return; }
      try {
        const p = await loadNotificationPrefs(session.id);
        if (!cancelled) setPrefs(p);
      } catch { /* default prefs apply */ }
      finally { if (!cancelled) setPrefsLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [session?.id]);

  async function setPref(key: keyof NotificationPrefs, val: boolean) {
    if (!session) return;
    const next = { ...prefs, [key]: val };
    setPrefs(next);
    setPrefMsg(null);
    try {
      await saveNotificationPrefs(session.id, next);
      setPrefMsg({ kind: "ok", text: "Saved." });
    } catch (err: any) {
      setPrefMsg({ kind: "err", text: err?.message || "Could not save." });
      // revert
      setPrefs(prefs);
    }
  }

  // -------- Display prefs --------
  const [density, setDensity] = useState<Density>(() => readStringPref<Density>(PREF_KEYS.density, "comfortable"));
  const [landing, setLanding] = useState<LandingRole>(() => readStringPref<LandingRole>(PREF_KEYS.landing, "auto"));
  useEffect(() => { writeStringPref(PREF_KEYS.density, density); }, [density]);
  useEffect(() => { writeStringPref(PREF_KEYS.landing, landing); }, [landing]);

  const tenantLabel = useMemo(() => "Mi6 Group", []);
  // D-023 / D-042 (2026-05-21): show role in ALL CAPS (AE / BDR / SE /
  // MANAGER / EXECUTIVE / MARKETING). The prior title-case branch
  // rendered "Ae" for AE users, which was the D-042 defect.
  //
  // D-056: When a manager is operating under "View As: Executive" (purely a
  // client-side navigation toggle — the session role doesn’t change), display
  // the effective view role so the user isn’t confused by seeing "MANAGER"
  // while the entire dashboard is in Executive mode.
  const rawRole = session?.role || "manager";
  const storedViewAs = (() => {
    try { return localStorage.getItem("aip:viewAs"); } catch { return null; }
  })();
  const effectiveViewRole = storedViewAs && storedViewAs !== rawRole ? storedViewAs : null;
  const roleLabel = effectiveViewRole
    ? `Operating as: ${effectiveViewRole.toUpperCase()} (base: ${rawRole.toUpperCase()})`
    : rawRole.toUpperCase();

  async function handleSignOut() {
    try { await logout(); } catch { /* ignore */ }
    setLocation("/login");
  }

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Settings" },
        { label: "Account" },
      ]}
      pageTitle="Account Settings"
      pageMeta="Profile, password, preferences"
    >
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 720px)", gap: 16 }}>
        {/* Profile */}
        <Card title="Profile">
          <Row label="Email"><ReadOnly value={session?.email || "—"} /></Row>
          <Row label="Role"><ReadOnly value={roleLabel} /></Row>
          <Row label="Tenant"><ReadOnly value={tenantLabel} /></Row>
          <Row label="Display name">
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              style={inputStyle}
              placeholder="e.g. Bryan Short"
            />
          </Row>
          <ActionRow>
            <PrimaryBtn onClick={saveProfile} disabled={profileBusy}>
              {profileBusy ? "Saving…" : "Save profile"}
            </PrimaryBtn>
            {profileMsg && <InlineMsg kind={profileMsg.kind}>{profileMsg.text}</InlineMsg>}
          </ActionRow>
        </Card>

        {/* Password */}
        <Card title="Change password">
          <Row label="Current password">
            <input type="password" autoComplete="current-password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} style={inputStyle} />
          </Row>
          <Row label="New password">
            <input type="password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} style={inputStyle} />
          </Row>
          <Row label="Confirm new password">
            <input type="password" autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} style={inputStyle} />
          </Row>
          <p style={hintStyle}>Minimum 8 characters. You stay signed in after the change.</p>
          <ActionRow>
            <PrimaryBtn onClick={changePassword} disabled={pwBusy}>
              {pwBusy ? "Updating…" : "Update password"}
            </PrimaryBtn>
            {pwMsg && <InlineMsg kind={pwMsg.kind}>{pwMsg.text}</InlineMsg>}
          </ActionRow>
        </Card>

        {/* Notifications */}
        <Card title="Notifications">
          {!prefsLoaded ? (
            <p style={hintStyle}>Loading preferences…</p>
          ) : (
            <>
              <Toggle label="Daily morning digest"
                description="Top accounts, open escalations, sprint pace each morning."
                checked={prefs.daily_digest} onChange={(v) => setPref("daily_digest", v)} />
              <Toggle label="Escalation alerts"
                description="Email when a deal hits a Tier 3 escalation."
                checked={prefs.escalations} onChange={(v) => setPref("escalations", v)} />
              <Toggle label="Weekly roll-up"
                description="Friday summary of team activity and pipeline movement."
                checked={prefs.weekly_rollup} onChange={(v) => setPref("weekly_rollup", v)} />
              <Toggle label="Support alerts"
                description="Direct emails when a support ticket assigned to you needs attention."
                checked={prefs.support_alerts} onChange={(v) => setPref("support_alerts", v)} />
              <p style={hintStyle}>Preferences are saved automatically to the server and honored by every scheduled email.</p>
              {prefMsg && <InlineMsg kind={prefMsg.kind}>{prefMsg.text}</InlineMsg>}
            </>
          )}
        </Card>

        {/* Display */}
        <Card title="Display">
          <Row label="Default landing">
            <select value={landing} onChange={(e) => setLanding(e.target.value as LandingRole)} style={inputStyle}>
              <option value="auto">Auto (based on my role)</option>
              <option value="manager">Manager dashboard</option>
              <option value="executive">Executive dashboard</option>
              <option value="ae">AE dashboard</option>
              <option value="bdr">BDR dashboard</option>
              <option value="marketing">Marketing dashboard</option>
              <option value="se">SE dashboard</option>
            </select>
          </Row>
          <Row label="Table density">
            <select value={density} onChange={(e) => setDensity(e.target.value as Density)} style={inputStyle}>
              <option value="comfortable">Comfortable</option>
              <option value="compact">Compact</option>
            </select>
          </Row>
          <p style={hintStyle}>Preferences are saved automatically.</p>
        </Card>

        {/* Sign out */}
        <Card title="Sign out">
          <p style={{ ...hintStyle, marginTop: 0 }}>
            Signs you out of this device. You'll need your password to sign back in.
          </p>
          <ActionRow>
            <DangerBtn onClick={handleSignOut}>Sign out</DangerBtn>
          </ActionRow>
        </Card>
      </div>
    </OperatorShell>
  );
}

/* ---------- small UI primitives, scoped to this page ---------- */

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: "#FFFFFF",
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.card,
        padding: 20,
      }}
    >
      <h2 style={{ margin: "0 0 12px", fontFamily: tokens.font.display, fontSize: 16, fontWeight: 700 }}>
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {children}
      </div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gridTemplateColumns: "180px 1fr", alignItems: "center", gap: 12 }}>
      <span style={{ fontSize: 13, color: tokens.color.textMuted }}>{label}</span>
      <div>{children}</div>
    </label>
  );
}

function ReadOnly({ value }: { value: string }) {
  return (
    <div style={{ fontSize: 13, color: tokens.color.textPrimary, padding: "8px 0" }}>
      {value}
    </div>
  );
}

function ActionRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", paddingTop: 4 }}>
      {children}
    </div>
  );
}

function PrimaryBtn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: tokens.color.black,
        color: "#FFFFFF",
        border: "none",
        borderRadius: 6,
        padding: "8px 14px",
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function DangerBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: tokens.color.red,
        color: "#FFFFFF",
        border: "none",
        borderRadius: 6,
        padding: "10px 18px",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        letterSpacing: "0.02em",
      }}
    >
      {children}
    </button>
  );
}

function Toggle({ label, description, checked, onChange }: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 3, width: 16, height: 16, accentColor: tokens.color.black }}
      />
      <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: tokens.color.textPrimary }}>{label}</span>
        <span style={{ fontSize: 12, color: tokens.color.textMuted, lineHeight: 1.4 }}>{description}</span>
      </span>
    </label>
  );
}

function InlineMsg({ kind, children }: { kind: "ok" | "err"; children: React.ReactNode }) {
  const isOk = kind === "ok";
  return (
    <span style={{
      fontSize: 12,
      color: isOk ? tokens.color.success : tokens.color.danger,
      background: isOk ? tokens.color.successBg : tokens.color.dangerBg,
      padding: "4px 10px",
      borderRadius: 4,
    }}>
      {children}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 360,
  padding: "8px 10px",
  border: `1px solid ${tokens.color.border}`,
  borderRadius: 6,
  fontSize: 13,
  fontFamily: "inherit",
  background: "#FFFFFF",
  color: tokens.color.textPrimary,
};

const hintStyle: React.CSSProperties = {
  margin: "4px 0 0",
  fontSize: 12,
  color: tokens.color.textMuted,
};
